// Single fetch wrapper for the admin API. Every request goes through here so
// the JWT header and error normalization live in exactly one place.

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';

/** localStorage keys the operator-login page writes the tokens under. */
export const JWT_STORAGE_KEY = 'admin_jwt';
export const REFRESH_STORAGE_KEY = 'admin_refresh';
/**
 * When the stored access token expires, in epoch ms on the BROWSER clock
 * (receipt time + the token's own `exp - iat`), so client/server clock skew
 * cannot make a fresh token look expired or an expired one look fresh.
 */
export const JWT_EXP_STORAGE_KEY = 'admin_jwt_exp';

/** Refresh this long before the recorded expiry rather than eating a 401. */
const REFRESH_LEAD_MS = 60_000;
/**
 * Session flag set by logout so the mock-mode auto-seeder (`seedMockJwt`) does
 * NOT immediately re-log-in. Lives in sessionStorage: a fresh tab clears it and
 * auto-login resumes. No effect against the real backend (which never seeds).
 */
export const MOCK_SIGNED_OUT_KEY = 'admin_mock_signed_out';

export function getToken(): string | null {
  return localStorage.getItem(JWT_STORAGE_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_STORAGE_KEY);
}

/** The token's lifetime from its `exp - iat` claims, or null if it lacks them. */
function tokenLifetimeMs(token: string): number | null {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(payload)) as { exp?: unknown; iat?: unknown };

    if (typeof claims.exp !== 'number' || typeof claims.iat !== 'number') return null;

    return (claims.exp - claims.iat) * 1000;
  } catch {
    return null;
  }
}

/**
 * Persists the operator access token and records when it expires. A token
 * without `exp`/`iat` (the mock-mode JWT), or one so short-lived that it would
 * already be inside the refresh lead right after every refresh, records
 * nothing and relies on the 401 fallback alone.
 */
export function setToken(token: string): void {
  localStorage.setItem(JWT_STORAGE_KEY, token);

  const lifetimeMs = tokenLifetimeMs(token);

  if (lifetimeMs === null || lifetimeMs <= 2 * REFRESH_LEAD_MS) {
    localStorage.removeItem(JWT_EXP_STORAGE_KEY);
  } else {
    localStorage.setItem(JWT_EXP_STORAGE_KEY, String(Date.now() + lifetimeMs));
  }
}

/** Ms until the recorded expiry (negative once past), or null when none is recorded. */
function msUntilExpiry(): number | null {
  const expiresAt = Number(localStorage.getItem(JWT_EXP_STORAGE_KEY));

  return expiresAt > 0 ? expiresAt - Date.now() : null;
}

/** Persists the operator refresh token (used for silent re-auth). */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_STORAGE_KEY, token);
}

/** Clears both tokens — called on logout and on an unrecoverable 401. */
export function clearToken(): void {
  localStorage.removeItem(JWT_STORAGE_KEY);
  localStorage.removeItem(REFRESH_STORAGE_KEY);
  localStorage.removeItem(JWT_EXP_STORAGE_KEY);
}

/** Drops the session and bounces to login (unless already there). */
function endSession(): void {
  clearToken();
  if (window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
}

/**
 * Exchanges the stored refresh token for a fresh access token.
 *
 * Shared in-flight promise: a burst of concurrent 401s triggers exactly one
 * refresh. Uses a bare `fetch` (not `request`) so it never recurses through
 * the 401 handler. Returns true on success.
 */
let refreshInFlight: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<boolean> => {
    const refreshToken = getRefreshToken();

    if (!refreshToken) return false;

    try {
      const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!res.ok) return false;

      const raw = (await res.json()) as Record<string, unknown>;
      // Unwrap the { success, code, data } envelope when present.
      const data = (raw.data as Record<string, unknown> | undefined) ?? raw;
      const accessToken = data.accessToken;

      if (typeof accessToken !== 'string') return false;

      setToken(accessToken);
      if (typeof data.refreshToken === 'string') setRefreshToken(data.refreshToken);

      return true;
    } catch {
      return false;
    }
  })();

  void refreshInFlight.finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/** Error carrying the HTTP status and the X-Request-Id correlation id. */
export class ApiError extends Error {
  readonly status: number;

  readonly requestId: string | null;

  constructor(message: string, status: number, requestId: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestId = requestId;
  }
}

/** What `authorizedFetch` sends, minus the Authorization header it adds itself. */
interface AuthorizedInit {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}

/** One send with the token stored right now. Network failures become ApiError(0). */
async function sendWithStoredToken(path: string, init: AuthorizedInit): Promise<Response> {
  const token = getToken();
  const headers: Record<string, string> = { ...init.headers };

  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    return await fetch(apiUrl(path), {
      method: init.method ?? 'GET',
      headers,
      body: init.body,
      signal: init.signal,
    });
  } catch (cause) {
    // An aborted request is not a network failure — re-throw it untouched so
    // TanStack Query can recognise the cancellation. The error is a DOMException
    // in browsers, but other fetch implementations use a plain Error; match on
    // the name regardless of the concrete type.
    if (
      typeof cause === 'object' &&
      cause !== null &&
      'name' in cause &&
      (cause as { name?: unknown }).name === 'AbortError'
    ) {
      throw cause;
    }
    throw new ApiError('Network request failed — check your connection.', 0, null);
  }
}

/**
 * Sends an authorized request and returns the final Response, keeping the
 * access token fresh in two layers (OBV-0048). It is the ONE place both layers
 * live: `request()` (JSON) and `fetchAuthenticatedBlob()` (attachment bytes)
 * both go through it, so a fix here reaches every authorized call.
 *
 * Each tab refreshes for itself; there is deliberately no cross-tab lock (no
 * BroadcastChannel, no storage lock — decided 2026-10-28, KB #822). Several
 * open admin tabs therefore refresh with the same rotating refresh token at
 * once, which works only because the API's `refresh-token.service.ts` accepts
 * a just-revoked token that still has an active successor in its family
 * (`isRevoked` + `hasActiveInFamily`, inside the grace window). If that path
 * ever becomes strict reuse detection, this needs a cross-tab lock.
 */
async function authorizedFetch(path: string, init: AuthorizedInit = {}): Promise<Response> {
  // Auth endpoints are exempt from both refresh paths — a 401 there is "wrong
  // password", not an expired session, and login must never wait on a refresh.
  const isAuthPath = path.startsWith('/admin/auth/') || path.startsWith('/auth/');

  // Refresh ahead of expiry instead of letting every open query 401 first: the
  // API files each 401 as a Sentry Issue (OBV-0048). Checked at the moment of
  // use, not on a timer — background tabs and sleeping laptops stall timers.
  // A failed refresh still sends while the token has time left (a network blip
  // inside the lead); once it has expired, sending would only file the 401.
  const untilExpiry = isAuthPath || !getToken() ? null : msUntilExpiry();

  if (untilExpiry !== null && untilExpiry <= REFRESH_LEAD_MS) {
    const refreshed = await refreshSession();

    if (!refreshed && (msUntilExpiry() ?? 0) <= 0) {
      endSession();
      throw new ApiError('Your session has expired — please sign in again.', 401, null);
    }
  }

  const response = await sendWithStoredToken(path, init);

  if (response.status !== 401 || isAuthPath) return response;

  // A 401 means the access token expired (or was revoked) despite the
  // proactive refresh above. Refresh once and send once more; if the refresh
  // fails or the retry also 401s, drop the session and bounce to login rather
  // than leaving the operator on a page where every action will 401. The
  // caller still gets the 401 response to build its own error from.
  if (await refreshSession()) {
    const retried = await sendWithStoredToken(path, init);

    if (retried.status !== 401) return retried;

    endSession();

    return retried;
  }

  endSession();

  return response;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await authorizedFetch(path, {
    method: options.method,
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  const requestId = response.headers.get('X-Request-Id');

  if (!response.ok) {
    let message = `Request failed (${response.status})`;

    try {
      const payload = (await response.json()) as { message?: string; error?: string };
      message = payload.message ?? payload.error ?? message;
    } catch {
      // non-JSON error body — keep the generic message
    }

    throw new ApiError(message, response.status, requestId);
  }

  if (response.status === 204) return undefined as T;

  const body: unknown = await response.json();

  // The API wraps successful responses in an envelope:
  //   { success, code, error, data }
  // Callers expect the payload directly, so unwrap `data` when the envelope
  // is present. Non-enveloped responses (e.g. MSW mocks) pass through.
  if (
    typeof body === 'object' &&
    body !== null &&
    'success' in body &&
    'data' in body
  ) {
    return (body as { data: T }).data;
  }

  return body as T;
}

export const apiClient = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
};

/** Serializes a params object into a query string (multi-value aware). */
function buildQuery(params: object): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.set(key, String(value));
    }
  }

  return search.toString();
}

/**
 * Fetches a private, auth-gated binary resource (e.g. a support-media attachment preview)
 * as a Blob, carrying the same Bearer header `apiClient` sends on every other request.
 *
 * A plain `<img src>`/`<video src>` pointed straight at an admin-gated API route can never
 * work: the browser never attaches an Authorization header to that kind of request, and a
 * bare relative path resolves against the SPA's own host, not the API's — fine for `request()`,
 * which is always same-origin-proxied in dev, but wrong once this is used directly as a media
 * element's `src`. Callers wrap the result in `URL.createObjectURL` and revoke it on cleanup.
 *
 * Goes through `authorizedFetch`, so a preview past expiry refreshes first, and a 401 refreshes
 * and retries once, exactly like every `request()` call (OBV-0048 amendment 2026-10-28).
 */
export async function fetchAuthenticatedBlob(path: string): Promise<Blob> {
  const response = await authorizedFetch(path);

  if (!response.ok) {
    throw new ApiError(
      `Could not load attachment (${response.status})`,
      response.status,
      response.headers.get('X-Request-Id')
    );
  }

  return response.blob();
}

/** Builds a /api/admin/fleet path with a query string from a params object. */
export function fleetPath(path: string, params?: object): string {
  const base = `/admin/fleet${path}`;

  if (!params) return base;

  const qs = buildQuery(params);

  return qs ? `${base}?${qs}` : base;
}

/** Builds a /api/admin/provisioning path with a query string. */
export function provisioningPath(path: string, params?: object): string {
  const base = `/admin/provisioning${path}`;

  if (!params) return base;

  const qs = buildQuery(params);

  return qs ? `${base}?${qs}` : base;
}

/** Builds a /api/admin/analytics path with a query string. */
export function analyticsPath(path: string, params?: object): string {
  const base = `/admin/analytics${path}`;

  if (!params) return base;

  const qs = buildQuery(params);

  return qs ? `${base}?${qs}` : base;
}

/** Builds a /api/admin/bootstrap-pool path. */
export function bootstrapPath(path = ''): string {
  return `/admin/bootstrap-pool${path}`;
}

/** Builds a /api/admin/config-signing path. */
export function configSigningPath(path = ''): string {
  return `/admin/config-signing${path}`;
}

/** Builds a /api/admin/support path with a query string — the bug-bounty operator surface. */
export function supportPath(path: string, params?: object): string {
  const base = `/admin/support${path}`;

  if (!params) return base;

  const qs = buildQuery(params);

  return qs ? `${base}?${qs}` : base;
}

/** Builds a /api/admin/store-metadata path with a query string. */
export function storePath(path: string, params?: object): string {
  const base = `/admin/store-metadata${path}`;

  if (!params) return base;

  const qs = buildQuery(params);

  return qs ? `${base}?${qs}` : base;
}

/** Absolute URL for an API path — used by EventSource (SSE), which bypasses fetch. */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}
